// Settings → Connectors → REST API (2026-09-30): API keys for scripts. The REST API is the MCP
// connector's own tools over HTTP (backend/mcp_rest.py) — same logic, audit and roles; a key acts
// AS a WMS account and signs its writes "Script <label> (<user>)". The key itself is shown once;
// the WMS keeps only its hash. Admin only. Claude, 2026-09-30.
import { useCallback, useEffect, useState } from 'react';
import { API, authFetch } from './api';
import { fmtWh } from './time';

const CARD = 'bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4';
const BASE = 'https://wms.motleydenim.ai/v1';

interface Key {
  id: number; prefix: string; label: string; username: string; scope: 'read' | 'write' | 'inbound';
  created_by: string | null; created_at: string; expires_at: string | null; last_used_at: string | null;
  revoked_at: string | null; revoked_by: string | null; calls_7d: number; errors_7d: number;
}
interface User { username: string; first_name?: string; last_name?: string; role: string; active: boolean; removed_at?: string | null }

export default function RestApiPanel() {
  const [keys, setKeys] = useState<Key[] | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ label: '', username: '', scope: 'read' as 'read' | 'write' | 'inbound', expires_days: '' });
  const [made, setMade] = useState<{ key: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/settings/api-keys`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setKeys(d.keys || []); setRate(d.rate_limit_per_min ?? null);
  }, []);
  useEffect(() => {
    load();
    authFetch(`${API}/users`).then(r => r.ok ? r.json() : []).then((d: any) => {
      const list: User[] = Array.isArray(d) ? d : (d.users || []);
      setUsers(list.filter(u => u.active && !u.removed_at));
    }).catch(() => {});
  }, [load]);

  const create = async () => {
    setBusy(true); setErr(''); setMade(null);
    const r = await authFetch(`${API}/settings/api-keys`, { method: 'POST', body: JSON.stringify({
      label: form.label.trim(), username: form.username, scope: form.scope,
      expires_days: form.expires_days ? Number(form.expires_days) : null }) });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setMade({ key: d.key, label: d.label });
    setForm({ label: '', username: '', scope: 'read', expires_days: '' });
    load();
  };
  const revoke = async (k: Key) => {
    if (!window.confirm(`Revoke the key "${k.label}" (${k.prefix}…)? Scripts using it stop at once.`)) return;
    const r = await authFetch(`${API}/settings/api-keys/${k.id}/revoke`, { method: 'POST', body: '{}' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    load();
  };
  const name = (u: string) => { const x = users.find(v => v.username === u); return x ? `${[x.first_name, x.last_name].filter(Boolean).join(' ') || u} (${x.role})` : u; };

  return (
    <div className="space-y-4">
      <div className={CARD}>
        <div className="text-white font-semibold text-sm mb-1">REST API — the connector's tools for scripts</div>
        <p className="text-gray-400 text-xs leading-relaxed">
          Scripts cannot speak MCP, so the same tools answer over plain HTTP: the same code, audit, rate limit
          ({rate ?? 30} calls a minute per key) and roles as the MCP connector. A key acts as the WMS account you choose.
          It can do what that account can do and nothing more, and it stops working if the account is deactivated.
          A read key cannot change anything; a write key signs its changes <span className="text-gray-200">Script &lt;label&gt; (&lt;account&gt;)</span>.
        </p>
        <div className="mt-3 text-xs text-gray-300 space-y-1 font-mono">
          <div>GET  {BASE}/tools <span className="text-gray-500 font-sans">— every tool, its arguments and whether it writes</span></div>
          <div>POST {BASE}/tools/order_lookup <span className="text-gray-500 font-sans">— body: {'{"order_number": "SK4284"}'}</span></div>
          <div className="text-gray-500 font-sans">Header: <span className="font-mono text-gray-300">Authorization: Bearer wmsk_…</span> · docs: <a className="text-blue-300 underline" href={`${BASE}/docs`} target="_blank" rel="noreferrer">{BASE}/docs</a> · reference: docs/REST-API.md</div>
        </div>
      </div>

      {made && (
        <div className="rounded-xl border border-emerald-700 bg-emerald-950/40 px-5 py-4">
          <div className="text-emerald-200 text-sm font-semibold">Key "{made.label}" — shown once</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 select-all break-all bg-black/40 text-emerald-100 px-3 py-2 rounded text-sm">{made.key}</code>
            <button onClick={() => { try { navigator.clipboard.writeText(made.key); } catch { /* */ } }}
              className="px-3 py-2 rounded bg-emerald-700 text-white text-sm">Copy</button>
          </div>
          <div className="text-emerald-300/80 text-xs mt-2">Store it in KeePass now (title "WMS API key — {made.label}", URL {BASE}). The WMS keeps only its hash; a lost key is revoked and replaced.</div>
          <button onClick={() => setMade(null)} className="mt-2 text-xs text-emerald-300 underline">I have stored it</button>
        </div>
      )}

      <div className={CARD}>
        <div className="text-white font-semibold text-sm mb-3">New key</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Label, e.g. CS assistant"
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
          <select value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white">
            <option value="">Acts as…</option>
            {users.map(u => <option key={u.username} value={u.username}>{name(u.username)}</option>)}
          </select>
          <select value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value as any })}
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white">
            <option value="read">Read only</option>
            <option value="write">Read and write</option>
            <option value="inbound">Read + book inbounds only</option>
          </select>
          <input value={form.expires_days} onChange={e => setForm({ ...form, expires_days: e.target.value.replace(/\D/g, '') })} placeholder="Expires in days (empty = never)"
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
        </div>
        <button disabled={busy || !form.label.trim() || !form.username} onClick={create}
          className="mt-3 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium">Make the key</button>
        {err && <div className="text-red-400 text-sm mt-2">{err}</div>}
      </div>

      <div className={CARD}>
        <div className="text-white font-semibold text-sm mb-2">Keys</div>
        {!keys ? <div className="text-gray-500 text-sm">Loading…</div> : keys.length === 0 ? <div className="text-gray-500 text-sm">No keys yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-xs text-left"><tr>
                <th className="py-1.5 pr-3">Label</th><th className="pr-3">Key</th><th className="pr-3">Acts as</th><th className="pr-3">Scope</th>
                <th className="pr-3">Made</th><th className="pr-3">Last used</th><th className="pr-3 text-right">Calls 7 d</th><th className="pr-3">Expires</th><th></th>
              </tr></thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id} className={`border-t border-gray-800 ${k.revoked_at ? 'text-gray-600' : 'text-gray-200'}`}>
                    <td className="py-1.5 pr-3 font-medium">{k.label}</td>
                    <td className="pr-3 font-mono text-xs">{k.prefix}…</td>
                    <td className="pr-3">{name(k.username)}</td>
                    <td className="pr-3">{k.scope === 'write' ? <span className="text-amber-300">read + write</span> : k.scope === 'inbound' ? <span className="text-sky-300">read + book inbounds</span> : 'read'}</td>
                    <td className="pr-3 text-xs whitespace-nowrap">{fmtWh(k.created_at, 'date')}{k.created_by ? ` · ${k.created_by}` : ''}</td>
                    <td className="pr-3 text-xs whitespace-nowrap">{k.last_used_at ? fmtWh(k.last_used_at, 'datetime') : '—'}</td>
                    <td className="pr-3 text-right text-xs">{k.calls_7d}{k.errors_7d ? <span className="text-red-400"> · {k.errors_7d} failed</span> : ''}</td>
                    <td className="pr-3 text-xs whitespace-nowrap">{k.revoked_at ? `revoked ${fmtWh(k.revoked_at, 'date')}` : k.expires_at ? fmtWh(k.expires_at, 'date') : 'never'}</td>
                    <td className="text-right">{!k.revoked_at && <button onClick={() => revoke(k)} className="text-xs text-red-300 hover:text-red-200">Revoke</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="text-[11px] text-gray-600 mt-2">Every call is in the connector's audit log (MCP tab) with the key's label. A password change or "sign out everywhere" of the account also ends its keys.</div>
      </div>
    </div>
  );
}
